/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\LogProfilePermissionsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Log Profiles module.
----------------------------------------------------------------*/


import { Bell, Search, Maximize2, Minimize2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { renderIcon } from '../utils';
import { LogPermissionButtonGroup, type PermKey } from './LogPermissionButtonGroup';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OrganizedItem = any;

interface Props {
    loading: boolean;
    organizeMenuItems: OrganizedItem[];
    expandedSections: Set<number>;
    expandedGroups: Set<number>;
    searchQuery: string;
    areAllExpanded: () => boolean;
    onSearchChange: (v: string) => void;
    onToggleAll: () => void;
    onToggleSection: (id: number) => void;
    onToggleGroup: (id: number) => void;
    getPermissionValue: (id: number, key: PermKey) => boolean;
    allChildrenHavePermission: (id: number, key: PermKey) => boolean;
    isPermissionInherited: (id: number, key: PermKey) => boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onPermissionChange: (id: number, key: PermKey, value: boolean, setFormData: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFormData: any;
}

const ChevronIcon = () => (
    <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
);

export function LogProfilePermissionsBox({
    loading, organizeMenuItems, expandedSections, expandedGroups, searchQuery, areAllExpanded,
    onSearchChange, onToggleAll, onToggleSection, onToggleGroup,
    getPermissionValue, allChildrenHavePermission, isPermissionInherited, onPermissionChange, setFormData,
}: Props) {
    const { t } = useI18n();

    return (
        <FormBox title="Log and Notification Permissions Matrix" icon={<Bell className="h-5 w-5" />}>
            {/* Toolbar */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <SimpleTooltip content={t('logProfiles.tooltips.permissionsSection')} position="top" maxWidth="300px">
                            <h3 className="text-lg font-medium text-foreground">{t('logProfiles.forms.permissionsSection')}</h3>
                        </SimpleTooltip>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('logProfiles.forms.logs')} C/E/D � {t('logProfiles.forms.notifications')} C/E/D �
                            <span className="text-blue-500">?</span> {t('logProfiles.forms.inheritedHint')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('logProfiles.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-64"
                            />
                            {searchQuery && (
                                <button type="button" onClick={() => onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={t('common.clear')}>�</button>
                            )}
                        </div>
                        <SimpleTooltip content={areAllExpanded() ? t('common.collapseAll') : t('common.expandAll')} position="top" maxWidth="200px">
                            <button type="button" onClick={onToggleAll} className="flex items-center justify-center p-2 text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors border border-primary/20">
                                {areAllExpanded() ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                            </button>
                        </SimpleTooltip>
                    </div>
                </div>
            </div>

            {/* Tree */}
            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            ) : (
                <div className="space-y-2">
                    {organizeMenuItems.map((section: OrganizedItem) => {
                        const isSectionExpanded = expandedSections.has(section.id);
                        return (
                            <div key={section.id} className="space-y-1">
                                {/* Section */}
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center justify-between p-3.5 border border-border rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer"
                                    onClick={() => onToggleSection(section.id)}>
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">{renderIcon(section.icon, 'w-5 h-5 text-primary')}</div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-foreground">{section.slug ? t(`menu.sections.${section.slug}`, section.title) : section.title}</h4>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">{section.children.length} groups</span>
                                            <motion.div animate={{ rotate: isSectionExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}><ChevronIcon /></motion.div>
                                        </div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <LogPermissionButtonGroup
                                            nodeId={section.id}
                                            getIsActive={(k) => allChildrenHavePermission(section.id, k as PermKey)}
                                            getIsInherited={(k) => isPermissionInherited(section.id, k as PermKey)}
                                            onToggle={(k, v) => onPermissionChange(section.id, k as PermKey, v, setFormData)}
                                        />
                                    </div>
                                </motion.div>

                                {/* Groups */}
                                <motion.div initial={false} animate={{ height: isSectionExpanded ? 'auto' : 0, opacity: isSectionExpanded ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} className="overflow-hidden">
                                    <div className="ml-4 space-y-1">
                                        {isSectionExpanded && section.children.length === 0 && (
                                            <div className="p-3 border border-dashed border-border rounded-lg bg-muted/10 text-sm text-muted-foreground">
                                                {t('logProfiles.emptySection', 'No modules in this section')}
                                            </div>
                                        )}
                                        {section.children.map((group: OrganizedItem) => {
                                            const isGroupExpanded = expandedGroups.has(group.id);
                                            const isLeafGroup = group.isLeafGroup || group.children.length === 0;
                                            return (
                                                <div key={group.id} className="space-y-1">
                                                    {/* Group */}
                                                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                                                        className={`flex items-center justify-between p-3 border border-border rounded-lg bg-secondary/5 hover:bg-secondary/10 transition-colors ${isLeafGroup ? '' : 'cursor-pointer'}`}
                                                        onClick={() => !isLeafGroup && onToggleGroup(group.id)}>
                                                        <div className="flex items-center gap-3 flex-1">
                                                            <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center">{renderIcon(group.icon, 'w-4 h-4 text-secondary')}</div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-medium text-foreground text-sm">{group.slug ? t(`menu.sections.${group.slug}`, group.title) : group.title}</h4>
                                                            </div>
                                                            {!isLeafGroup && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-muted-foreground">{group.children.length} items</span>
                                                                    <motion.div animate={{ rotate: isGroupExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                                                                        <svg className="w-3 h-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                                    </motion.div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <LogPermissionButtonGroup
                                                                nodeId={group.id}
                                                                getIsActive={(k) => isLeafGroup ? getPermissionValue(group.id, k as PermKey) : allChildrenHavePermission(group.id, k as PermKey)}
                                                                getIsInherited={(k) => isPermissionInherited(group.id, k as PermKey)}
                                                                onToggle={(k, v) => onPermissionChange(group.id, k as PermKey, v, setFormData)}
                                                            />
                                                        </div>
                                                    </motion.div>

                                                    {/* Items */}
                                                    {!isLeafGroup && (
                                                        <motion.div initial={false} animate={{ height: isGroupExpanded ? 'auto' : 0, opacity: isGroupExpanded ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} className="overflow-hidden">
                                                            <div className="ml-4 space-y-1">
                                                                {group.children.map((item: OrganizedItem) => (
                                                                    <motion.div key={item.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                                                                        className="p-3 border border-border rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="flex items-center gap-3 flex-1">
                                                                                <div className="w-6 h-6 rounded bg-muted/20 flex items-center justify-center">{renderIcon(item.icon, 'w-3 h-3 text-muted-foreground')}</div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <h4 className="font-medium text-foreground text-sm">{item.slug ? t(`menu.sections.${item.slug}`, item.title) : item.title}</h4>
                                                                                </div>
                                                                            </div>
                                                                            <LogPermissionButtonGroup
                                                                                nodeId={item.id}
                                                                                getIsActive={(k) => getPermissionValue(item.id, k as PermKey)}
                                                                                getIsInherited={(k) => isPermissionInherited(item.id, k as PermKey)}
                                                                                onToggle={(k, v) => onPermissionChange(item.id, k as PermKey, v, setFormData)}
                                                                            />
                                                                        </div>
                                                                    </motion.div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            </div>
                        );
                    })}
                </div>
            )}
        </FormBox>
    );
}
